// VERIFICATION FAILURE CLASSIFICATION (#1515) — which HTTP statuses mean "the key is
// dead", which mean "try again", and how long to wait before the retry.
// Split out of `../byok.ts`, which is now the barrel over this directory.

// TYPE-ONLY, on purpose: `value-objects/llm-credential` imports VALUES from this
// directory and reads them at its own init (`PROVIDER_WIRE` over `BYOKProvider`),
// so a value import back is a TDZ cycle. The reason/status vocabulary identity
// is pinned by value in `byok.test.ts` instead.
import type { LlmKeyFailureReason } from '../../value-objects/llm-credential';
import { BYOKStatus } from './vocabulary';

/**
 * The kind of failure a provider verify attempt can return. The single seam
 * that decides whether the row goes to `invalid` (needs a human) or keeps its
 * state + stamps `verification_retry_at` (retry).
 *
 *   - `'auth'`     — provider rejected the CREDENTIAL (`401`/`403`). Terminal:
 *     the row flips to `invalid` and stays there until a human rotates.
 *   - `'transient'`— the attempt did not settle the question: network error
 *     (`status=0`), `5xx`, a throttle (`429`), a timeout (`408`), or any status
 *     nobody has enumerated below. The credential is UNPROVEN, not bad — keep
 *     the row's state and stamp `verification_retry_at` so `cron.byok_reverify`
 *     asks again.
 *   - `'terminal'` — the provider judged the REQUEST and the answer cannot
 *     change: the enumerated {@link TERMINAL_VERIFY_STATUS} set. `markKeyInvalid`,
 *     which also RETRACTS any retry stamp.
 *   - `'endpoint_mismatch'` — the models GET has no route under this `base_url`
 *     (the enumerated {@link ENDPOINT_MISMATCH_VERIFY_STATUS} set: `404`/`405`).
 *     A verdict on the PROBE, not on the key: the endpoint may serve inference
 *     and nothing else, so the caller runs the inference probe, which grades
 *     quota/park/transient itself. NEVER `invalid` from this arm (plan 102 slice
 *     04, K2 — the prod Alibaba Token Plan key on `…/apps/anthropic` answered
 *     `404 Not support` to the models GET while `/v1/messages` answered `429`
 *     quota, i.e. authenticated, and sat `invalid` until hand-fixed).
 *
 * Named `transient`, not `transport`, since #2086: a `429` is a cooldown, not a
 * network fault, and the arm now carries both. The lifecycle OUTCOME the API
 * renders is still `transport_failed`. The AUDIT is split by what happened to the
 * key (plan 102 slice 01): a GRADED failure that moves `byok_keys.status` — a 402,
 * an exhaustion 429, a quota — writes `billing.key.status_changed` with the
 * `from`/`to` pair, and `billing.key.transport_failed` now covers only a real
 * transport failure (network, 5xx) where the status did not move.
 *
 * Pure — the verify path calls it on every verdict and the cost is one lookup.
 * The seam is here, not in `@developerz/billing`, because the retry-vs-rotate
 * axis is a platform policy, not a transport detail.
 */
export type VerifyFailureKind = 'auth' | 'transient' | 'terminal' | 'endpoint_mismatch';

/** The statuses that are a verdict on the CREDENTIAL. Closed — see {@link classifyVerifyFailure}. */
export type AuthVerifyStatus = 401 | 403;

/**
 * The only statuses that mean "the credential is rejected". `Record` over a
 * closed union, so a status cannot join this arm without a sentence saying why.
 */
export const AUTH_VERIFY_STATUS: Record<AuthVerifyStatus, string> = {
  401: 'the provider rejected this API key',
  403: 'the provider refused this API key',
};

/** The statuses whose answer a retry cannot change. Closed — see {@link TERMINAL_VERIFY_STATUS}. */
export type TerminalVerifyStatus = 400 | 410 | 422;

/**
 * The ENUMERATED permanent client errors, each mapped to the clause a customer
 * reads. `Record` over a closed union is the guard: a status joins the terminal
 * arm only by being added to {@link TerminalVerifyStatus} *and* given a reason
 * here, which fails to compile until both are done.
 *
 * The property that qualifies a member — not the list, which will change:
 * **the verify request is byte-identical on every attempt** (a bare `GET` at a
 * static URL with static headers, `verifyUrlFor` + `buildAuthHeaders` in
 * `@developerz/billing`), so a status that judges the REQUEST can only ever be
 * re-earned, and only a human editing the row's `base_url`/`model` changes the
 * answer. What each member is in practice:
 *
 * - `400` — the endpoint rejects the request itself. The shape a wrong wire
 *   produces: z.ai's Anthropic-wire path answers `400` to a GOOD key (#1759).
 * - `410` — RFC 9110 §15.5.11 defines it as *permanent* unavailability. The one
 *   status whose own specification says not to ask again.
 * - `422` — the request was understood and semantically refused (the validation
 *   refusal the marketplace hosts return).
 *
 * `404`/`405` left this table in plan 102 slice 04 (K2): "no models route under
 * this base_url" is true of a working inference-only endpoint too, so it is
 * {@link ENDPOINT_MISMATCH_VERIFY_STATUS}, not a verdict.
 *
 * Deliberately NOT here, and the reason each is transient instead:
 * `402` (payment required — it heals when the customer tops up, without
 * touching the key), `408`/`409`/`429` (a timeout, a race, a cooldown — all
 * "ask again"), `5xx` and `0`. Terminality costs a customer a manual re-paste;
 * a retry of leg 1 costs one models-list `GET`, and the PAID model probe only
 * runs after leg 1 returns `2xx`, so a wrongly-retried status spends nothing.
 * That asymmetry is why terminal is the enumerated arm and not the default
 * (#2086: `429` reached the default arm and three prod keys paid for it).
 */
export const TERMINAL_VERIFY_STATUS: Record<TerminalVerifyStatus, string> = {
  400: 'the provider rejected the verification request itself',
  410: 'the provider says this endpoint is permanently gone',
  422: 'the provider could not process the verification request',
};

/** The statuses that say "no models route here" — closed, see {@link ENDPOINT_MISMATCH_VERIFY_STATUS}. */
export type EndpointMismatchVerifyStatus = 404 | 405;

/**
 * The models GET found no route: `404` (no `/models` under this `base_url` — an
 * inference-only path such as Alibaba's `/apps/anthropic`, or a site root) and
 * `405` (the path exists but does not serve `GET`). Neither says anything about
 * the CREDENTIAL, so neither may write `invalid`: the caller falls through to the
 * inference probe, which is the question the key actually has to answer. If that
 * probe cannot reach a route either, IT grades the key — this arm only refuses to
 * guess. Same `Record`-over-a-closed-union guard as the tables above.
 */
export const ENDPOINT_MISMATCH_VERIFY_STATUS: Record<EndpointMismatchVerifyStatus, string> = {
  404: 'the provider has no models endpoint at this base_url',
  405: 'the provider does not serve the models request at this base_url',
};

/** Is `status` a missing-probe-route answer? Narrows for {@link ENDPOINT_MISMATCH_VERIFY_STATUS} lookups. */
export function isEndpointMismatchVerifyStatus(
  status: number,
): status is EndpointMismatchVerifyStatus {
  return Object.hasOwn(ENDPOINT_MISMATCH_VERIFY_STATUS, status);
}

/** Is `status` in the enumerated permanent set? Narrows for {@link TERMINAL_VERIFY_STATUS} lookups. */
export function isTerminalVerifyStatus(status: number): status is TerminalVerifyStatus {
  return Object.hasOwn(TERMINAL_VERIFY_STATUS, status);
}

/** Is `status` a verdict on the credential? Narrows for {@link AUTH_VERIFY_STATUS} lookups. */
export function isAuthVerifyStatus(status: number): status is AuthVerifyStatus {
  return Object.hasOwn(AUTH_VERIFY_STATUS, status);
}

/**
 * Classify a provider HTTP status from a verify attempt. Pure; the verify path
 * calls it once per `VerifyResult.status` and routes to `markKeyInvalid`
 * (`auth`/`terminal`), `recordTransportFailure` (`transient`), or the inference
 * probe (`endpoint_mismatch`).
 *
 * TERMINAL IS ENUMERATED AND TRANSIENT IS THE DEFAULT — the inversion is the
 * whole point (#2086). An unrecognised status is the ABSENCE of evidence that
 * the credential is permanently bad, never evidence that it is; filing it as
 * terminal costs a customer a manual re-paste of a key that was never bad,
 * while filing it as transient costs one more free `GET` two minutes later.
 * `429` is the status that proved it: it fell through the old default and three
 * production keys were marked `invalid`/`revoked` over a rate limit.
 *
 * Status `0` is the library's own transport-failure marker (the fetch threw —
 * DNS, refused, timeout, abort) and lands in `transient` like everything else
 * unenumerated. It is not special-cased, because it no longer needs to be.
 */
export function classifyVerifyFailure(status: number): VerifyFailureKind {
  if (isAuthVerifyStatus(status)) return 'auth';
  if (isEndpointMismatchVerifyStatus(status)) return 'endpoint_mismatch';
  if (isTerminalVerifyStatus(status)) return 'terminal';
  return 'transient';
}

/**
 * FLOOR (ms) between transient-flagged verify retries (#1515). The stamp is the
 * next-attempt deadline itself — no exponential schedule encoded in another
 * column — so the value is constant on purpose. 30s: long enough to outlast an
 * honest TCP-level retry storm on the upstream's side, short enough that a
 * healthy provider is back in the pool within one or two `cron.byok_reverify`
 * ticks (`BYOK_REVERIFY_CRON` = every 2 min,
 * `apps/worker/src/cron/byok-reverify.ts` — the sweep that actually re-probes a
 * stamped row; nothing fires between ticks, so this floor bounds how STALE a
 * stamp is allowed to be, not how often a real re-probe happens). A persistent
 * blip converges to "one re-probe every 2 min" — a rate the provider's own
 * cooldown comfortably absorbs.
 *
 * A floor is not a schedule: when the provider STATES a cooldown, it wins. See
 * {@link verifyRetryDelayMs}.
 */
export const VERIFY_RETRY_BACKOFF_MS = 30_000;

/**
 * Ceiling (ms) on a provider-stated cooldown. One hour. A `Retry-After` is
 * upstream-controlled text, so it is clamped where it is read rather than
 * trusted: a header of `86400` (or a mis-set date years out) would park the key
 * out of the pool for a day with nothing but a stamp to explain it, and a key
 * that never gets looked at again is the very failure #2086 is about, reached
 * from the other side.
 */
export const VERIFY_RETRY_MAX_BACKOFF_MS = 60 * 60 * 1000;

/**
 * Parse an RFC 9110 `Retry-After` into milliseconds from `now`. Both forms:
 * delta-seconds (`120`) and an HTTP-date (`Wed, 21 Oct 2015 07:28:00 GMT`).
 *
 * Returns `null` for absent, blank, or unparseable — NEVER `0`. An unreadable
 * header must mean "the provider said nothing" (so {@link verifyRetryDelayMs}
 * applies the floor), not "retry immediately", which would turn a garbled
 * header into the tight re-probe loop the header exists to prevent. A date
 * already in the past is a real statement (`0`), unlike an unreadable one.
 *
 * BOTH forms are matched by shape before `Date.parse` ever sees the string, and
 * that is not belt-and-braces: `Date.parse('-5')` answers a real timestamp (in
 * the year 5 BC), so handing junk straight to it produces a date in the past,
 * which is exactly the `0` this function must never invent from an unreadable
 * header.
 */
export function parseRetryAfterMs(raw: string | null | undefined, now: Date): number | null {
  const value = raw?.trim() ?? '';
  if (value === '') return null;
  // delta-seconds: a non-negative integer. `-5`, `1.5` and `2s` are not this form.
  if (/^\d+$/.test(value)) {
    const seconds = Number(value);
    return Number.isSafeInteger(seconds * 1000) ? seconds * 1000 : null;
  }
  // IMF-fixdate (RFC 9110 §5.6.7) — the one date form providers send.
  if (!/^[A-Za-z]{3}, \d{2} [A-Za-z]{3} \d{4} \d{2}:\d{2}:\d{2} GMT$/.test(value)) return null;
  const at = Date.parse(value);
  if (Number.isNaN(at)) return null;
  return Math.max(0, at - now.getTime());
}

/**
 * How long until the next verify attempt, given what the provider said. The ONE
 * place the floor, the provider's own cooldown, and the ceiling meet:
 * `max(floor, min(stated, ceiling))`.
 *
 * The provider's number only ever LENGTHENS the wait. A `Retry-After: 1` must
 * not shorten the floor below what `cron.byok_reverify` can honour anyway (it
 * ticks every 2 min), and a `Retry-After: 86400` must not park the key for a
 * day — see {@link VERIFY_RETRY_MAX_BACKOFF_MS}.
 */
export function verifyRetryDelayMs(retryAfterMs?: number | null): number {
  if (retryAfterMs === null || retryAfterMs === undefined) return VERIFY_RETRY_BACKOFF_MS;
  const bounded = Math.min(retryAfterMs, VERIFY_RETRY_MAX_BACKOFF_MS);
  return Math.max(VERIFY_RETRY_BACKOFF_MS, bounded);
}

/**
 * How long until the next verify attempt, given the REASON a failure was graded
 * into (#2844) — the sibling of {@link verifyRetryDelayMs} for the failures the
 * runtime can NAME (`classifyLlmFailure`), whose recovery timescales genuinely
 * differ:
 *
 * - `rate_limited` — a throttle. Delegates to {@link verifyRetryDelayMs}: the
 *   30s floor, the provider's own stated cooldown up to the ceiling.
 * - `credits_exhausted` — a spent wallet. It refills when a human tops up, not
 *   in 30 seconds, so the wait is the CEILING: re-probing an empty wallet at
 *   the floor is ~2,880 authenticated GETs a day against the customer's own
 *   provider for an answer only they can change. At one hour it is still
 *   self-healing — the first probe after a top-up promotes the row to `valid`.
 *
 * The comparison is against {@link BYOKStatus.CreditsExhausted}, not a fresh
 * literal, for the same reason that constant exists at all: it IS the
 * `LlmKeyFailureReason` value (pinned in `byok.test.ts`), and this module
 * cannot import that one as a value without a cycle.
 *
 * Pure; both stamp-writers (`markKeyCooling`'s callers) feed it the reason they
 * are about to persist, so the schedule and the status name the same fact.
 */
export function verifyRecoverDelayMs(
  reason: LlmKeyFailureReason,
  retryAfterMs?: number | null,
): number {
  if (reason === BYOKStatus.CreditsExhausted) return VERIFY_RETRY_MAX_BACKOFF_MS;
  return verifyRetryDelayMs(retryAfterMs);
}
